import { CARRIER_LABEL, COUNTRY_ARG } from "./constants";

// Moneda y locale por país. El dashboard nunca mezcla países (siempre filtra por
// uno), así que cada vista de costos usa una sola moneda. PAN: USD es de curso
// legal y el más usado para display (PAB está a la par); ajustable si producto
// prefiere PAB.
const CURRENCY_BY_COUNTRY: Record<
  string,
  { locale: string; currency: string }
> = {
  ARG: { locale: "es-AR", currency: "ARS" },
  COL: { locale: "es-CO", currency: "COP" },
  PAN: { locale: "es-PA", currency: "USD" },
};

// Formatters de moneda memoizados por país (crear Intl.NumberFormat es caro).
const currencyFormatterCache = new Map<string, Intl.NumberFormat>();

const getCurrencyFormatter = (country?: string): Intl.NumberFormat => {
  const key =
    country && CURRENCY_BY_COUNTRY[country] ? country : COUNTRY_ARG;
  const cached = currencyFormatterCache.get(key);
  if (cached) return cached;
  const { locale, currency } = CURRENCY_BY_COUNTRY[key];
  const formatter = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  currencyFormatterCache.set(key, formatter);
  return formatter;
};

const NUMBER_FORMATTER = new Intl.NumberFormat("es-AR");

const DECIMAL_FORMATTER = new Intl.NumberFormat("es-AR", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

// country por default ARG → callers existentes sin país siguen mostrando ARS.
export const formatCurrency = (value: number, country?: string): string =>
  getCurrencyFormatter(country).format(value);

// Código de moneda (ARS/COP/USD) para leyendas ("<código> acumulado…").
export const currencyCodeFor = (country?: string): string =>
  (country && CURRENCY_BY_COUNTRY[country]?.currency) ||
  CURRENCY_BY_COUNTRY[COUNTRY_ARG].currency;

export const formatNumber = (value: number): string =>
  NUMBER_FORMATTER.format(value);

export const formatRate = (value: number): string => `${Math.round(value)}%`;

export const formatDays = (value: number | null): string => {
  if (value === null || value === undefined) return "—";
  return `${DECIMAL_FORMATTER.format(value)} días`;
};

// Solo el número (sin unidad), para tarjetas que muestran "días" como unidad
// aparte y más chica (ver TimingKpis).
export const formatDaysValue = (value: number | null): string =>
  value === null || value === undefined
    ? "—"
    : DECIMAL_FORMATTER.format(value);

// Nombre de carrier para mostrar en el dashboard. Traduce los identificadores
// que requieren etiqueta (OWN_FLEET -> "FLETE PROPIO") y cae al name del
// backend, o al id como último recurso. Usa CARRIER_LABEL (mayúsculas) para que
// "Flete propio" se vea consistente con el resto de los OLs, que el backend ya
// devuelve en mayúsculas (ANDREANI, OCA, ...). Centraliza el `name || id` que
// estaba repetido en varias secciones.
export const formatCarrierName = (
  name?: string | null,
  id?: string | null,
): string => {
  const raw = name || id || "";
  return CARRIER_LABEL[raw] || raw;
};

// Devuelve la variación porcentual respecto del período anterior.
// - null  → no hay base de comparación pero sí valor actual (crecimiento desde
//           cero): se muestra como "Nuevo" en vez de un engañoso +0%.
// - 0     → sin cambios (ambos en cero o iguales).
export const formatDelta = (
  current: number,
  previous: number,
): number | null => {
  if (previous === 0) return current > 0 ? null : 0;
  return Math.round(((current - previous) / previous) * 100);
};
